import {Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatSelectChange} from "@angular/material/select";
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from "rxjs/operators";
import {
  CLIENT_TYPE,
  CLIENT_TYPE_OFWCA_FROM_OUTSIDE,
  DictService,
} from "../../services/dict.service";
import {ListResponse} from "../../interface/response/list-response";
import {DOCUMENT} from "@angular/common";
import {ReplaySubject} from "rxjs";
import {GlobalSearchService} from "../../services/global-search.service";
import {Dictionary} from "../../interface/dictionary";

@Component({
  selector: 'app-dict-ofwcas-partners',
  templateUrl: './dict-ofwcas-partners.component.html',
  styleUrls: ['./dict-ofwcas-partners.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictOfwcasPartnersComponent)}
  ]
})
export class DictOfwcasPartnersComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() onlyActive = false;
  @Output() selectedItem: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedItems: EventEmitter<any[]> = new EventEmitter<any[]>();

  readonly CLIENT_TYPE_OFWCA_FROM_OUTSIDE = CLIENT_TYPE_OFWCA_FROM_OUTSIDE;
  readonly CLIENT_TYPE = CLIENT_TYPE;

  externalControl = true;
  disabled = false;
  items: any[] = [];
  dict = null;
  itemsSubject: ReplaySubject<ListResponse<any>> = new ReplaySubject<ListResponse<any>>(1);
  value?: string = null;
  selectedValue?: string = null;
  ofwcasPartnerFilterCtrl: FormControl;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  constructor(
    private globalSearchService: GlobalSearchService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
    private dictService: DictService,
  ) {
    this.ofwcasPartnerFilterCtrl = fb.control('');
    this.dictService.get(CLIENT_TYPE_OFWCA_FROM_OUTSIDE, CLIENT_TYPE)
      .subscribe((dict: Dictionary) => {
      this.dict = dict;
    });
  }

  onChange: any = () => { };
  onTouched: any = () => { };
  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: any) => {
        if (val) {
          let exists = false;
          this.items.forEach((item: any) => {
            if (item.uuid === val[0]) {
              exists = true;
            }
          });

          if (!exists && val) {
            this.globalSearchService
              .loadNotCountedList(
                0,
                9999,
                {in$uuid: val},
                this.itemsSubject
              );
          }
        }
      });

    this.ofwcasPartnerFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.globalSearchService.loadNotCountedList(0, 2, {
            context: val,
            statusKeys: this.onlyActive ? 'active' : null,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.globalSearchService
            .loadNotCountedList(
              0,
              9999,
              {in$uuid: this.control.value},
              this.itemsSubject
            );
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<any>) => {
        this.items = result.items;

        if (this.multiple) {
          const selected = [];
          this.items.forEach((item: any) => {
            if (this.control.value?.includes(item.uuid)) {
              selected.push(item);
            }

            this.selectedItems.emit(selected);
          });
        } else {
          this.items.forEach((item: any) => {
            if (item.uuid === this.control.value) {
              this.selectedItem.emit(item);
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }
  writeValue(value: any): void {
    if (!value) {
      this.selectedValue = '';
    }
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

}
